import type { ColumnDefinition } from "../columnFilters/columnDefinition";

// Sentinel id for the (otherwise nameless) time column in columnWidths.
// Distinct from any columnId, which is the only other key shape.
export const TIME_COLUMN_ID = "__time__";

// Default time column width when not pinned. Expressed in px (rather than the
// equivalent 12.5rem) so the same number feeds both the grid template and the
// min-width floor below — a rem literal in the template could not be summed.
export const DEFAULT_TIME_WIDTH_PX = 200;

// Minimum draggable width. Enforced by computeResizedWidth, which the resize
// handle calls on every pointermove, and by the minmax() floor in the grid
// template, which stops an unpinned column being squeezed below it.
export const MIN_COLUMN_WIDTH_PX = 60;

// Gap between columns. Must match --column-gap in LogPanel.module.css; the
// panel feeds this value back into that variable so the two cannot drift.
export const COLUMN_GAP_PX = 16;

export type ColumnWidths = Record<string, number>;

export function buildGridTemplate(
  dynamicColumns: ColumnDefinition[],
  widths: ColumnWidths,
): string {
  const time = `${widths[TIME_COLUMN_ID] ?? DEFAULT_TIME_WIDTH_PX}px`;
  const rest = dynamicColumns.map((c) => {
    const w = widths[c.columnId];
    if (w !== undefined) {
      return `${w}px`;
    }
    // Unpinned columns still flex to fill a roomy panel, but never shrink past
    // the floor. The floor alone doesn't produce scrolling — .scroll-list's
    // paint containment would just clip the overrun — so it works in concert
    // with the min-width from computeMinGridWidth, which widens the row box to
    // hold these tracks.
    return `minmax(${MIN_COLUMN_WIDTH_PX}px, 1fr)`;
  });
  return [time, ...rest].join(" ");
}

// Narrowest the row can be drawn without violating any column's floor. The
// panel applies this as a min-width on .scroll-list, which is what actually
// produces horizontal scrolling: that element has paint containment, so tracks
// overflowing its box are clipped and contribute no scrollable width. Growing
// the box instead lets a too-narrow panel scroll rather than shrinking columns
// past the point of legibility.
export function computeMinGridWidth(
  dynamicColumns: ColumnDefinition[],
  widths: ColumnWidths,
): number {
  const time = widths[TIME_COLUMN_ID] ?? DEFAULT_TIME_WIDTH_PX;
  const columnsTotal = dynamicColumns.reduce(
    (sum, c) => sum + (widths[c.columnId] ?? MIN_COLUMN_WIDTH_PX),
    time,
  );
  // One gap between each adjacent pair: time + N dynamic columns => N gaps.
  return columnsTotal + dynamicColumns.length * COLUMN_GAP_PX;
}

// On the first drag, freeze every still-flexing column to its current rendered
// width so that subsequent drags only move the dragged column. Ids are
// [time, ...dynamicColumns] in render order; `measure(i)` returns the rendered
// width of the i-th column, or undefined if it isn't measurable (e.g. the
// header has fewer children than columns).
export function snapshotMissingWidths(
  dynamicColumns: ColumnDefinition[],
  current: ColumnWidths,
  measure: (index: number) => number | undefined,
): ColumnWidths {
  const ids = [TIME_COLUMN_ID, ...dynamicColumns.map((c) => c.columnId)];

  let next: ColumnWidths | null = null;
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    if (current[id] !== undefined) {
      continue;
    }
    const width = measure(i);
    // Reject non-positive measurements as well as absent ones. A zero from a
    // cell that exists but isn't laid out would otherwise be pinned as a 0px
    // track and handed back as the drag's start width, collapsing the column
    // and making the first pointermove jump up from nothing.
    if (width === undefined || width <= 0) {
      continue;
    }
    if (next === null) {
      next = { ...current };
    }
    next[id] = width;
  }

  return next ?? current;
}

/**
 * Hand the space a removed column occupied back to the columns that remain.
 *
 * A removed column leaves its pinned width behind as an entry no current
 * columnId matches, so the departed widths are recoverable from `widths` alone
 * — no record of the previous column set is needed. Each survivor grows in
 * proportion to its current width, which preserves the relative layout the
 * user dragged out while closing the dead strip at the end of the row.
 *
 * Returns `widths` unchanged when there is nothing to reclaim, so callers can
 * use reference equality to skip redundant state updates.
 */
export function reclaimRemovedWidths(
  dynamicColumns: ColumnDefinition[],
  widths: ColumnWidths,
): ColumnWidths {
  if (Object.keys(widths).length === 0) {
    return widths;
  }

  // With no data columns left there is no proportional layout worth keeping;
  // fall back to the default rather than stretching time across the panel.
  if (dynamicColumns.length === 0) {
    return {};
  }

  const present = [TIME_COLUMN_ID, ...dynamicColumns.map((c) => c.columnId)];
  const presentIds = new Set(present);
  const departed = Object.keys(widths).filter((id) => !presentIds.has(id));
  if (departed.length === 0) {
    return widths;
  }

  const pinned = present.filter((id) => widths[id] !== undefined);
  const survivors: ColumnWidths = {};
  for (const id of pinned) {
    survivors[id] = widths[id];
  }

  // An unpinned survivor is an fr track, so it already soaks up freed space on
  // its own; redistributing here would fight it. Just drop the dead entries.
  if (pinned.length < present.length) {
    return survivors;
  }

  // A departing column frees its own width plus the gap that separated it, so
  // the row still spans exactly what it spanned before.
  const freed = departed.reduce(
    (sum, id) => sum + widths[id] + COLUMN_GAP_PX,
    0,
  );
  const pinnedTotal = pinned.reduce((sum, id) => sum + widths[id], 0);

  let distributed = 0;
  pinned.forEach((id, index) => {
    if (index === pinned.length - 1) {
      // The last column absorbs the rounding remainder, so repeated removals
      // can't drift the row a pixel narrower each time.
      survivors[id] = widths[id] + (freed - distributed);
      return;
    }
    const share = Math.round((widths[id] / pinnedTotal) * freed);
    distributed += share;
    survivors[id] = widths[id] + share;
  });

  return survivors;
}

// Convenience: build a measure() function from a header row element whose
// direct children are the column header cells in order.
export function measureFromHeader(
  headerEl: HTMLElement,
): (index: number) => number | undefined {
  return (i) => {
    const child = headerEl.children[i] as HTMLElement | undefined;
    return child ? child.getBoundingClientRect().width : undefined;
  };
}

export function computeResizedWidth(
  startWidth: number,
  startClientX: number,
  currentClientX: number,
): number {
  const next = startWidth + (currentClientX - startClientX);
  return Math.max(MIN_COLUMN_WIDTH_PX, next);
}
